
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { APP_VERSION } from '../utils/version';
import NoTranslate from '../components/NoTranslate';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import { formatDisplayDateRange, resolveLeaveTypeName, formatDisplayDate } from '../utils/localeFormat';

function MyApplications() {
  const leaveTypes = useLeaveTypes();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    year: new Date().getFullYear()
  });
  const [certificateUpload, setCertificateUpload] = useState({
    applicationId: null,
    file: null,
    uploading: false
  });

  useEffect(() => {
    fetchApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.year) params.append('year', filters.year);

      const response = await api.get(`/applications/my-applications?${params}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Application history fetch error:', error);
      toast.error('Failed to fetch application history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'Pending', class: 'badge-warning' },
      approved: { text: 'Approved', class: 'badge-success' },
      rejected: { text: 'Rejected', class: 'badge-danger' }
    };
    const statusInfo = statusMap[status] || { text: status, class: 'badge-secondary' };
    return (
      <NoTranslate className={`badge ${statusInfo.class}`}>{statusInfo.text}</NoTranslate>
    );
  };

  const formatDateRange = formatDisplayDateRange;

  const getTotalDays = (details) => {
    if (!details) return 0;
    return details.reduce((total, detail) => total + detail.hours / 8, 0);
  };

  const handleCertificateUpload = async (application) => {
    if (!certificateUpload.file) {
      toast.error('Please select a certificate file');
      return;
    }

    try {
      setCertificateUpload(prev => ({ ...prev, uploading: true }));

      const toYmd = (d) => {
        if (d == null || d === '') {
          return '';
        }
        const s = typeof d === 'string' ? d : new Date(d).toISOString();
        return String(s).slice(0, 10);
      };

      const fileFormData = new FormData();
      fileFormData.append('certificate', certificateUpload.file);
      fileFormData.append('application_id', application.id);
      fileFormData.append('start_date', toYmd(application.start_date));
      fileFormData.append('end_date', toYmd(application.end_date));

      await api.post('/upload/certificate', fileFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Certificate uploaded successfully');
      setCertificateUpload({ applicationId: null, file: null, uploading: false });
      fetchApplications(); // Refresh to show attachment flag
    } catch (error) {
      console.error('Certificate upload error:', error);
      toast.error('Failed to upload certificate');
    } finally {
      setCertificateUpload(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>My Applications</h1>
          <small className="text-muted">{APP_VERSION}</small>
        </div>
        <Link to="/apply" className="btn btn-primary">
          New Application
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <h3>Filters</h3>
        <div className="grid grid-3">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              name="status"
              className="form-control"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Year</label>
            <select
              name="year"
              className="form-control"
              value={filters.year}
              onChange={handleFilterChange}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Search Results</label>
            <div className="search-result">
              {applications.length} applications found
            </div>
          </div>
        </div>
      </div>

      {/* Application List */}
      {applications.length > 0 ? (
        <div className="applications-list">
          {applications.map(application => (
            <div key={application.id} className="application-card card">
              <div className="application-header">
                <div className="application-title">
                  <NoTranslate as="h4">{resolveLeaveTypeName(application, leaveTypes)}</NoTranslate>
                  {getStatusBadge(application.status)}
                </div>
                <div className="application-date">
                  Applied: {formatDisplayDate(application.applied_at)}
                </div>
              </div>

              <div className="application-details">
                <div className="detail-row">
                  <span className="detail-label">Application ID:</span>
                  <span>#{application.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Period:</span>
                  <span>{formatDateRange(application.start_date, application.end_date)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Days Taken:</span>
                  <span>{getTotalDays(application.details)} days</span>
                </div>
                {application.reason && (
                  <div className="detail-row">
                    <span className="detail-label">Reason:</span>
                    <span>{application.reason}</span>
                  </div>
                )}
                {application.requested_approver_name && (
                  <div className="detail-row">
                    <span className="detail-label">Approver:</span>
                    <NoTranslate>
                      {application.requested_approver_name}
                      {application.requested_approver_department && ` (${application.requested_approver_department})`}
                    </NoTranslate>
                  </div>
                )}
                {application.reviewed_at && (
                  <div className="detail-row">
                    <span className="detail-label">Review Date:</span>
                    <span>
                      {formatDisplayDate(application.reviewed_at)}
                      {application.reviewed_by_name && (
                        <NoTranslate>{` (${application.reviewed_by_name})`}</NoTranslate>
                      )}
                    </span>
                  </div>
                )}
                {application.admin_notes && (
                  <div className="detail-row">
                    <span className="detail-label">Admin Comment:</span>
                    <span className="admin-notes">{application.admin_notes}</span>
                  </div>
                )}
                
                {/* Certificate Upload for approved medical leave */}
                {application.status === 'approved' && application.leave_type_code === 'MEDICAL' && (
                  <div className="certificate-section">
                    <div className="detail-row">
                      <span className="detail-label">Medical Certificate:</span>
                      <span className="certificate-status">
                        {application.certificate_filename ? (
                          <NoTranslate className="badge badge-success">📎 Attached</NoTranslate>
                        ) : (
                          <NoTranslate className="badge badge-secondary">Not Attached</NoTranslate>
                        )}
                      </span>
                    </div>
                    
                    {!application.certificate_filename && (
                      <div className="certificate-upload">
                        <div className="upload-form">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setCertificateUpload(prev => ({
                              ...prev,
                              applicationId: application.id,
                              file: e.target.files[0]
                            }))}
                            className="form-control form-control-sm"
                          />
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleCertificateUpload(application)}
                            disabled={certificateUpload.uploading || !certificateUpload.file}
                          >
                            {certificateUpload.uploading ? 'Uploading...' : 'Upload Certificate'}
                          </button>
                        </div>
                        <small className="text-muted">Upload PDF, JPG, or PNG file (max 5MB)</small>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Detailed Schedule Display */}
              {application.details && application.details.length > 0 && (
                <div className="schedule-details">
                  <h5>Detailed Schedule</h5>
                  <div className="schedule-grid">
                    {application.details.map((detail, index) => (
                      <div key={index} className="schedule-item">
                        <div className="schedule-date">
                          {formatDisplayDate(detail.leave_date, {
                            month: 'short',
                            day: 'numeric',
                            weekday: 'short'
                          })}
                        </div>
                        <div className="schedule-period">
                          {detail.period === 'full' && 'Full Day'}
                          {detail.period === 'am' && 'Morning'}
                          {detail.period === 'pm' && 'Afternoon'}
                          <span className="schedule-hours">({detail.hours}h)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(application.certificate_filename || application.certificate_google_drive_url) && (
                <div className="certificate-info">
                  <span className="detail-label">Attachment:</span>
                  {application.certificate_google_drive_url ? (
                    <a 
                      href={application.certificate_google_drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View Certificate (Google Drive)
                    </a>
                  ) : (
                    <a 
                      href={`/uploads/${application.certificate_filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View Certificate
                    </a>
                  )}
                </div>
              )}

              {(application.compensation_attachment_filename ||
                application.compensation_attachment_google_drive_url) &&
                application.leave_type_code === 'COMPENSATION' && (
                <div className="certificate-info">
                  <span className="detail-label">Supporting screenshot:</span>
                  {application.compensation_attachment_google_drive_url ? (
                    <a
                      href={application.compensation_attachment_google_drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View (Google Drive)
                    </a>
                  ) : (
                    <a
                      href={`/uploads/${application.compensation_attachment_filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View screenshot
                    </a>
                  )}
                </div>
              )}

              {(application.other_paid_leave_document_filename ||
                application.other_paid_leave_document_google_drive_url) &&
                application.leave_type_code === 'OTHER_PAID' && (
                <div className="certificate-info">
                  <span className="detail-label">Supporting document:</span>
                  {application.other_paid_leave_document_google_drive_url ? (
                    <a
                      href={application.other_paid_leave_document_google_drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View (Google Drive)
                    </a>
                  ) : (
                    <a
                      href={`/uploads/${application.other_paid_leave_document_filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      📎 View document
                    </a>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="application-actions">
                {application.status === 'pending' && (
                  <Link 
                    to={`/apply?edit=${application.id}`}
                    className="btn btn-outline-primary btn-sm"
                  >
                    Edit
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state card">
          <div className="text-center">
            <h3>No Application History</h3>
            <p className="text-muted">You haven't submitted any leave applications yet.</p>
            <Link to="/apply" className="btn btn-primary">
              Create Your First Application
            </Link>
          </div>
        </div>
      )}

      <style>{`
        .applications-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .application-card {
          border-left: 4px solid #007bff;
        }
        
        .application-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .application-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .application-title h4 {
          margin: 0;
          color: #495057;
        }
        
        .application-date {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .application-details {
          margin-bottom: 1rem;
        }
        
        .detail-row {
          display: flex;
          margin-bottom: 0.5rem;
          gap: 0.5rem;
        }
        
        .detail-label {
          font-weight: 500;
          color: #495057;
          min-width: 120px;
        }
        
        .admin-notes {
          background-color: #f8f9fa;
          padding: 0.5rem;
          border-radius: 4px;
          border-left: 3px solid #ffc107;
        }
        
        .schedule-details {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }
        
        .schedule-details h5 {
          margin-bottom: 0.5rem;
          color: #495057;
        }
        
        .schedule-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 0.5rem;
        }
        
        .schedule-item {
          background-color: #f8f9fa;
          padding: 0.5rem;
          border-radius: 4px;
          text-align: center;
          font-size: 0.9rem;
        }
        
        .schedule-date {
          font-weight: 500;
          color: #495057;
        }
        
        .schedule-period {
          color: #007bff;
          margin-top: 0.25rem;
        }
        
        .schedule-hours {
          font-size: 0.8rem;
          color: #6c757d;
        }
        
        .certificate-info {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .certificate-link {
          color: #007bff;
        }
        
        .certificate-section {
          margin-top: 1rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 6px;
        }
        
        .certificate-upload {
          margin-top: 0.5rem;
        }
        
        .upload-form {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          margin-bottom: 0.25rem;
        }
        
        .upload-form input[type="file"] {
          flex: 1;
        }
        
        .certificate-status .badge {
          font-size: 0.8rem;
          text-decoration: none;
        }
        
        .certificate-link:hover {
          text-decoration: underline;
        }
        
        .application-actions {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }
        
        .empty-state {
          padding: 3rem;
        }
        
        .search-result {
          padding: 0.5rem;
          background-color: #f8f9fa;
          border-radius: 4px;
          font-weight: 500;
          color: #495057;
        }
        
        @media (max-width: 768px) {
          .application-header {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .application-title {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .detail-row {
            flex-direction: column;
          }
          
          .detail-label {
            min-width: auto;
          }
          
          .schedule-grid {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          }
        }
      `}</style>
    </div>
  );
}

export default MyApplications;
